'use client';

import { useCallback } from 'react';

import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@kit/ui/accordion';

import { trackChatSuiteFeatureInterest } from '~/lib/analytics/chatsuite-events';

const faqItems = [
  {
    question: 'What is ChatSuite?',
    answer:
      'ChatSuite is a multi-agent AI platform for WhatsApp Business. It combines automated customer support, CRM, e-commerce, and developer tools in one platform. Businesses use ChatSuite to automate conversations, manage customer relationships, and sell products directly through WhatsApp.',
  },
  {
    question: 'How does AI auto-reply work?',
    answer:
      'ChatSuite uses specialized AI agents that handle different types of conversations. FAQ agents answer common questions instantly, sales agents qualify leads and recommend products, and order agents handle purchase flows. The system learns from your past conversations and knowledge base to improve responses over time.',
  },
  {
    question: 'Can I keep my WhatsApp number?',
    answer:
      'Yes. ChatSuite supports both the official Meta Cloud API (which uses your existing WhatsApp Business number) and Baileys (which connects to your personal or business WhatsApp directly). You can migrate your existing number without losing your chat history.',
  },
  {
    question: 'What is the difference between Baileys and Meta Cloud API?',
    answer:
      'Baileys connects directly to WhatsApp Web, giving you full access to all WhatsApp features including groups, status, and media. It works with any WhatsApp number. The Meta Cloud API is the official business API from Meta, designed for high-volume messaging with verified business profiles and green badges. ChatSuite supports both options.',
  },
  {
    question: 'How does pricing work?',
    answer:
      'ChatSuite offers four tiers: Starter ($49/mo) for small businesses getting started with automation, Growth ($129/mo) for teams needing multi-agent AI and knowledge base, Business ($299/mo) for companies requiring e-commerce and developer SDK, and Enterprise (custom) for large organizations with SLA requirements. All plans include a 14-day free trial.',
  },
  {
    question: 'Is it GDPR and DSG compliant?',
    answer:
      'Yes. ChatSuite is built with data protection in mind. We comply with EU GDPR, Swiss DSG (nDSG/FADP), and Austrian DSG 2000. Data is processed in European data centers, consent management is built into every flow, and all customer data can be exported or deleted on request.',
  },
];

export function ChatSuiteFaq() {
  const handleQuestionClick = useCallback((question: string) => {
    trackChatSuiteFeatureInterest({ feature: `faq:${question}` });
  }, []);

  return (
    <section id="faq" className="bg-background w-full px-4 py-16 sm:py-24">
      <div className="mx-auto max-w-3xl">
        <div className="mb-12 text-center">
          <p className="mb-3 text-sm font-semibold tracking-[0.2em] text-[#25D366] uppercase">
            FAQ
          </p>
          <h2 className="text-foreground text-3xl font-bold tracking-tight sm:text-4xl">
            Frequently asked questions
          </h2>
        </div>

        <Accordion>
          {faqItems.map((item, index) => (
            <AccordionItem key={item.question} value={String(index)}>
              <AccordionTrigger
                className="text-foreground text-left text-base font-medium"
                onClick={() => handleQuestionClick(item.question)}
              >
                {item.question}
              </AccordionTrigger>
              <AccordionContent>
                <p className="text-muted-foreground">{item.answer}</p>
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </div>
    </section>
  );
}
